import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  ExtractedTargetInfo,
  TargetInfoType,
  TargetTypeEnum,
} from '@ais-ui/websockets';
import { Port } from '../modules/Map/AISMapContext';
import { AtonHost } from '@ais-ui/api';

// Determines what panel to show when a port is selected
export type PortSelectionMode = 'routes' | 'nextport';

interface SelectionStateContextProps {
  targetInfoDetailsList: ExtractedTargetInfo[];
  setTargetInfoDetailsList: Dispatch<SetStateAction<ExtractedTargetInfo[]>>;
  selectedPorts: Port[];
  setSelectedPorts: Dispatch<SetStateAction<Port[]>>;
  portSelectionMode: PortSelectionMode;
  setPortSelectionMode: Dispatch<SetStateAction<PortSelectionMode>>;
  currentTargetInfo: TargetInfoType | null;
  currentTargetType: TargetTypeEnum | null;
  goToNextCard: () => void;
  currentIndex: number;
  aton: AtonHost | null;
  setAton: Dispatch<SetStateAction<AtonHost | null>>;
  currentPort: Port | null;
  showVesselDetailCardMobile: boolean;
  setShowVesselDetailCardMobile: Dispatch<SetStateAction<boolean>>;
}

const SelectionStateContext = createContext<
  SelectionStateContextProps | undefined
>(undefined);

interface SelectionStateProviderProps {
  children: ReactNode;
}

export const SelectionStateProvider = ({
  children,
}: SelectionStateProviderProps) => {
  const [targetInfoDetailsList, setTargetInfoDetailsList] = useState<
    ExtractedTargetInfo[]
  >([]);

  const [aton, setAton] = useState<AtonHost | null>(null);

  const [selectedPorts, setSelectedPorts] = useState<Port[]>([]);
  
  // Controls what panel to show when a port is selected (default: routes)
  const [portSelectionMode, setPortSelectionMode] = useState<PortSelectionMode>('routes');
  
  // Mobile-only: controls whether vessel detail card is shown on mobile (default: false, only track shown)
  const [showVesselDetailCardMobile, setShowVesselDetailCardMobile] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const goToNextCard = () => {
    if (currentIndex < targetInfoDetailsList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const { currentTargetInfo, currentTargetType } = useMemo(() => {
    if (targetInfoDetailsList.length > 0) {
      if (currentIndex >= targetInfoDetailsList.length) {
        setCurrentIndex(0);
      }
      const currentTargetInfo =
        targetInfoDetailsList[currentIndex]?.extractedTargetInfo?.[0] || null;
      const currentTargetType =
        targetInfoDetailsList[currentIndex]?.extractedTargetInfoType || null;
      return { currentTargetInfo, currentTargetType };
    }
    return { currentTargetInfo: null, currentTargetType: null };
  }, [currentIndex, targetInfoDetailsList]);

  const currentPort = useMemo(() => {
    if (selectedPorts.length > 0 && currentIndex < selectedPorts.length) {
      return selectedPorts[currentIndex];
    }
    return null;
  }, [currentIndex, selectedPorts]);

  const value = useMemo(() => ({
    targetInfoDetailsList,
    setTargetInfoDetailsList,
    selectedPorts,
    setSelectedPorts,
    portSelectionMode,
    setPortSelectionMode,
    currentTargetInfo,
    currentTargetType,
    goToNextCard,
    currentIndex,
    aton,
    setAton,
    currentPort,
    showVesselDetailCardMobile,
    setShowVesselDetailCardMobile,
  }), [
    targetInfoDetailsList,
    setTargetInfoDetailsList,
    selectedPorts,
    setSelectedPorts,
    portSelectionMode,
    setPortSelectionMode,
    currentTargetInfo,
    currentTargetType,
    goToNextCard,
    currentIndex,
    aton,
    setAton,
    currentPort,
    showVesselDetailCardMobile,
    setShowVesselDetailCardMobile,
  ]);

  return (
    <SelectionStateContext.Provider value={value}>
      {children}
    </SelectionStateContext.Provider>
  );
};

export const useSelectionState = () => {
  const context = useContext(SelectionStateContext);
  if (!context) {
    throw new Error(
      'useSelectionState must be used within a SelectionStateProvider'
    );
  }
  return context;
};
